import type { Player, Round, Score, TeeSet } from "../types.js";
import type { Contest, Format, RoundResult } from "./types.js";
import {
  buildScoreMap,
  computeHandicaps,
  holeCount,
  listNames,
  scoringHoles,
  netOnHole,
} from "./util.js";

interface SkinsConfig {
  skinValue?: number;
  carryover?: boolean;
}

/** What happened on one hole, as the pot walk saw it. */
type HoleOutcome =
  /** Not every player's score is in, so the hole has no result yet. */
  | { kind: "gap" }
  /** Won outright, taking `pot` (which includes anything carried in). */
  | { kind: "won"; winnerId: string; pot: number }
  /** Tied, with the pot rolling forward — `pot` is what now carries. */
  | { kind: "carried"; pot: number }
  /** Tied with carryover off, so the skin is simply not won. */
  | { kind: "lost" };

/**
 * The single walk through a Skins round.
 *
 * A rolling pot can't be reconstructed from a final number, so scoring and the
 * hole-by-hole breakdown both have to replay the whole sequence — and when they were two
 * separate replays they disagreed: `score` honoured `onlyHoles` and `explainHoles` did
 * not, so the breakdown narrated a pot building on holes that paid nobody. One walk,
 * used by both, is the only way they can't drift apart again.
 *
 * The round's full hole sequence is walked rather than a pre-filtered one: a hole with no
 * data has to be visible as a gap, not silently removed, or an earlier tied hole's pot
 * would roll straight onto a later hole's winner.
 */
function walkPot(
  round: Round,
  course: TeeSet,
  players: Player[],
  scores: Score[],
  onlyHoles: number[] | undefined,
): Map<number, HoleOutcome> {
  const config = (round.formatConfig ?? {}) as SkinsConfig;
  const skinValue = config.skinValue ?? 1;
  const carryover = config.carryover ?? true;

  const holes = scoringHoles(round, course);
  const allowed = onlyHoles ? new Set(onlyHoles) : null;
  const scoreMap = buildScoreMap(scores);
  const handicaps = computeHandicaps(round, course, players);

  const outcomes = new Map<number, HoleOutcome>();
  let pot = 0;

  for (const hole of holes) {
    if (allowed && !allowed.has(hole)) {
      pot = 0; // a gap voids whatever was building, rather than bridging over it
      outcomes.set(hole, { kind: "gap" });
      continue;
    }

    const nets = players
      .map((p) => ({ id: p.id, net: netOnHole(scoreMap, handicaps, p.id, hole) }))
      .filter((e): e is { id: string; net: number } => e.net != null);

    if (nets.length < players.length) {
      pot = 0; // hole not fully entered yet — same as above, a gap, not a decided tie
      outcomes.set(hole, { kind: "gap" });
      continue;
    }

    pot += skinValue;
    const best = Math.min(...nets.map((e) => e.net));
    const winners = nets.filter((e) => e.net === best);

    if (winners.length === 1) {
      outcomes.set(hole, { kind: "won", winnerId: winners[0]!.id, pot });
      pot = 0;
    } else if (carryover) {
      outcomes.set(hole, { kind: "carried", pot });
    } else {
      outcomes.set(hole, { kind: "lost" });
      pot = 0;
    }
  }

  return outcomes;
}

export const skins: Format = {
  id: "skins",
  name: "Skins",
  scope: "individual",
  uses: { handicapAllowance: true },
  entry: "perPlayer",
  defaults: { handicapAllowance: 100 },
  configSchema: {
    type: "object",
    properties: {
      skinValue: { type: "number", default: 1, title: "Points per skin" },
      carryover: {
        type: "boolean",
        default: true,
        title: "Tied hole carries over",
        description: "When a hole is tied, its skin rolls into the next hole instead of being lost.",
      },
    },
  },
  perHoleSchema: null,

  score({ round, course, players, scores, onlyHoles }): RoundResult {
    const points: Record<string, number> = {};
    for (const p of players) points[p.id] = 0;

    for (const outcome of walkPot(round, course, players, scores, onlyHoles).values()) {
      if (outcome.kind === "won") {
        points[outcome.winnerId] = (points[outcome.winnerId] ?? 0) + outcome.pot;
      }
    }

    return { roundId: round.id, points, provisional: Boolean(onlyHoles) };
  },

  /** Reports what happened on each hole rather than only the total — a rolling pot is
   * impossible to reconstruct from a final number, which is why this format needs the
   * hook. Reads the same walk `score` does, so the two can never disagree. */
  explainHoles({ round, course, players, scores, onlyHoles, playerId }) {
    const notes = new Map<number, string>();

    for (const [hole, outcome] of walkPot(round, course, players, scores, onlyHoles)) {
      switch (outcome.kind) {
        case "gap":
          break; // nothing decided here, so there is nothing to say about it
        case "won":
          notes.set(
            hole,
            outcome.winnerId === playerId
              ? `won the skin, +${outcome.pot}`
              : "skin to another player",
          );
          break;
        case "carried":
          notes.set(hole, `tied — pot carries (${outcome.pot})`);
          break;
        case "lost":
          notes.set(hole, "tied — skin lost");
          break;
      }
    }
    return notes;
  },

  /** Everyone against everyone on every hole. The winner comes from the same walk
   * `score` reads — only the nets shown alongside it are re-derived, and those carry no
   * state to drift. */
  explainContests({ round, course, players, scores, onlyHoles }): Contest[] {
    const scoreMap = buildScoreMap(scores);
    const handicaps = computeHandicaps(round, course, players);
    const outcomes = walkPot(round, course, players, scores, onlyHoles);
    if (outcomes.size === 0) return [];

    const columns = players.map((p) => ({ id: p.id, label: p.name, playerIds: [p.id] }));
    const won: Record<string, number> = {};

    const holes = [...outcomes].map(([hole, outcome]) => {
      if (outcome.kind === "won") won[outcome.winnerId] = (won[outcome.winnerId] ?? 0) + 1;
      return {
        hole,
        sides: players.map((p) => ({
          id: p.id,
          label: p.name,
          playerIds: [p.id],
          value: netOnHole(scoreMap, handicaps, p.id, hole),
          won: outcome.kind === "won" && outcome.winnerId === p.id,
        })),
        note:
          outcome.kind === "gap"
            ? "not all scores in"
            : outcome.kind === "won"
              ? `skin to ${players.find((p) => p.id === outcome.winnerId)?.name ?? outcome.winnerId}, +${outcome.pot}`
              : outcome.kind === "carried"
                ? `tied — pot carries (${outcome.pot})`
                : "tied — skin lost",
      };
    });

    const taken = Object.entries(won);
    const most = taken.length > 0 ? Math.max(...taken.map(([, n]) => n)) : 0;
    const leaders = taken.filter(([, n]) => n === most).map(([id]) => id);
    const nameOf = (id: string) => players.find((p) => p.id === id)?.name ?? id;
    const named = leaders.map(nameOf);
    const standing =
      most === 0
        ? "No skins won yet"
        : named.length === 1
          ? `${named[0]} leads on ${most} skin${most === 1 ? "" : "s"}`
          : `${listNames(named)} tied on ${most}`;

    return [{ id: "skins", title: "Skins", columns, holes, standing }];
  },

  describeRules(round, _players, course) {
    const config = (round.formatConfig ?? {}) as SkinsConfig;
    const skinValue = config.skinValue ?? 1;
    const carryover = config.carryover ?? true;
    return [
      `Skins across ${holeCount(round, course)} holes, each worth ${skinValue} point${skinValue === 1 ? "" : "s"}.`,
      carryover
        ? "A tied hole carries its skin forward to the next hole."
        : "A tied hole's skin is simply not won by anyone.",
      `Handicap allowance: ${round.handicapAllowance}%.`,
    ];
  },
};
